import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';

import { SnackbarTemplateType } from '@ninety/platform/models/enums/snackbar-template-type';
import { Meeting } from '@ninety/platform/models/meetings/meeting';
import { MeetingMessage } from '@ninety/platform/models/meetings/meeting-message';
import { MeetingMessageAction } from '@ninety/platform/models/meetings/meeting-message-action';
import { ChannelService } from '@ninety/platform/services';
import {
  NotificationActions,
  RealTimeActions,
  selectAllUsers,
  selectCurrentUserIsManageeOrAbove,
  selectCurrentUserId,
  selectLanguage,
} from '@ninety/platform/state';
import { NotifyService } from '@ninety/ui/legacy/core/index';

import { MeetingService } from '../../_shared/services/meeting.service';
import { MeetingsTangentDialogComponent } from '../../meetings-tangent-dialog/meetings-tangent-dialog.component';
import { MeetingRealTimeActions, MeetingsPageActions } from '../meetings.actions';
import { MeetingsStateSelectors } from '../meetings.selectors';

/**
 * Broadcasts received from other users
 */

@Injectable()
export class MeetingsRealTimeSubscriptionsEffects {
  private readonly actions$ = inject(Actions);
  private readonly channelService = inject(ChannelService);
  private readonly store = inject(Store);
  private readonly meetingService = inject(MeetingService);
  private readonly dialog = inject(MatDialog);
  private readonly notifyService = inject(NotifyService);

  /** Watches for changes to meetings on the currently selected team */
  receiveMeetingStateChangeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === 'meeting-state-change'),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectTeamId),
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
      ]),
      filter(([{ message }, teamId, currentMeeting]) =>
        this.filterToCurrentMeeting(message.document as MeetingMessage, teamId, currentMeeting)
      ),
      map(() => MeetingsPageActions.getActiveMeetingInfo())
    )
  );

  /** Only fetch current meeting info if there's a change to meeting state */
  filterToCurrentMeeting(message: MeetingMessage, teamId: string, meeting: Meeting): boolean {
    return (
      message?.teamId === teamId &&
      (message?.meeting?.inProgress !== meeting?.inProgress || message?.meeting?.paused !== meeting?.paused)
    );
  }

  /**
   * Gets channel presence in response to several actions for users on the meetings page who haven't joined the meeting yet -
   * used to display users who are present in the meeting on the active-meeting card.
   */
  getChannelPresence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingRealTimeActions.authorizeChannelSuccess, MeetingRealTimeActions.meetingStateChange),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectMeetingStatus)]),
      filter(([, meeting]) => !!meeting),
      switchMap(([, meeting]) => {
        const meetingChannelId = `meeting-${meeting._id}`;
        return this.channelService
          .getPresenceByChannelId(meetingChannelId)
          .pipe(map(attendees => MeetingRealTimeActions.setMeetingPresence({ attendees })));
      })
    )
  );

  /**
   * Authorizes the channel for the current user to check presence by the getChannelPresence$ effect above.
   * This usually occurs when subscribing to a channel - but in this case the user isn't subscribed yet.
   */
  getMeetingChannelAuthToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.getActiveMeetingInfoSuccess),
      concatLatestFrom(() => [this.store.select(selectCurrentUserId)]),
      filter(([{ meeting }]) => !!meeting),
      switchMap(([{ meeting }, userId]) =>
        this.channelService
          .authorizeChannelId(userId, `meeting-${meeting._id}`)
          .pipe(map(() => MeetingRealTimeActions.authorizeChannelSuccess()))
      )
    )
  );

  /**
   * Checks if a presenter is present after joining a meeting and re-assigns to current user otherwise.
   */
  checkMeetingPresenter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingRealTimeActions.checkMeetingPresenterExists),
        switchMap(({ meeting }) => this.channelService.getPresenceByChannelId(`meeting-${meeting._id}`)),
        concatLatestFrom(() => [
          this.store.select(selectCurrentUserId),
          this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
          this.store.select(selectCurrentUserIsManageeOrAbove),
        ]),
        filter(([presence, userId, meeting, isManageeOrAbove]) =>
          this.meetingNeedsPresenter(presence, userId, meeting, isManageeOrAbove)
        ),
        switchMap(([, userId]) => this.meetingService.updateCurrentMeeting({ presenterUserId: userId }))
      ),
    { dispatch: false }
  );

  private meetingNeedsPresenter(
    presence: string[],
    userId: string,
    meeting: Meeting,
    isManageeOrAbove: boolean
  ): boolean {
    return !presence.some(u => u === meeting.presenterUserId) && meeting.presenterUserId !== userId && isManageeOrAbove;
  }

  /**
   * Redirects to meeting home when a meeting presenter deletes or suspends a meeting
   */
  meetingDeletedOrSuspended$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(
        ({ message }) =>
          message.messageType === MeetingMessageAction.Suspended || message.messageType === MeetingMessageAction.Delete
      ),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        this.store.select(selectAllUsers),
        this.store.select(selectLanguage),
      ]),
      filter(([{ message }, meeting]) => message.document === meeting?._id),
      tap(() => {
        this.meetingService.clearMeeting();
        this.meetingService.navigateToMeetingsHome();
      }),
      map(([{ message, emitterId }, , users, language]) => {
        const user = users.find(u => u._id === emitterId)?.fullName;
        const action = message.messageType === MeetingMessageAction.Suspended ? 'suspended' : 'deleted';
        const notifyMessage = `${language.meeting.item} was ${action} by ${user}`;
        return NotificationActions.notify({ message: notifyMessage, duration: 3000 });
      })
    )
  );

  // Meeting Tangent Effects
  openTangentDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingRealTimeActions.openMeetingTangentDialog),
        exhaustMap(() => this.dialog.open(MeetingsTangentDialogComponent, { disableClose: true }).afterClosed())
      ),
    { dispatch: false }
  );

  closeTangentDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingRealTimeActions.tangentDialogClosed),
        tap(() => {
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );
  /**
   * Shows a popup for the presenter
   */
  meetingTangentPresenter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === MeetingMessageAction.MeetingTangent),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        this.store.select(selectCurrentUserId),
      ]),
      filter(([{ message }, meeting, userId]) => {
        const isPresenter = meeting?.presenterUserId === userId;
        const timestamp = (message.document as MeetingMessage)?.timestamp;
        if (!isPresenter || !timestamp) return false;
        const messageTime = new Date(timestamp).getTime();
        const now = Date.now();
        return (now - messageTime) / 1000 <= 5; // within last 5 seconds
      }),
      switchMap(() => of(MeetingRealTimeActions.openMeetingTangentDialog()))
    )
  );

  /**
   * Shows a notification for non presenter
   */
  meetingTangentNonPresenter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === MeetingMessageAction.MeetingTangent),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        this.store.select(selectCurrentUserId),
      ]),
      filter(([, meeting, userId]) => meeting?.presenterUserId !== userId),
      map(() => {
        return MeetingRealTimeActions.showMeetingTangentNotification();
      })
    )
  );

  openTangentToastNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingRealTimeActions.showMeetingTangentNotification),
        tap(() =>
          this.notifyService.notifyWithTemplate(
            SnackbarTemplateType.warning,
            {
              message: 'Tangent called.',
              iconKey: 'warning-triangle',
              iconVariant: 'duotone',
              link: {},
            },
            5000
          )
        )
      ),
    { dispatch: false }
  );
}
